import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuSearch, LuClipboardX, LuTrash2, LuPencil, LuX,
} from 'react-icons/lu';

// Exportada para reaproveitamento em outras telas do módulo Avaliações (ex.
// OperatorPerformanceReportPage.js) — única fonte de verdade da lógica e das
// cores do semáforo de avaliação, não duplicar limiares/paleta em outro lugar.
export const getSemaforo = (score) => {
  if (score >= 80) return { cls: 'verde',    label: 'Parabéns', color: 'var(--success)', bg: 'var(--success-soft)' };
  if (score >= 60) return { cls: 'amarelo',  label: 'Atenção',  color: 'var(--warning)', bg: 'var(--warning-soft)' };
  return              { cls: 'vermelho', label: 'Crítico',  color: 'var(--danger)', bg: 'var(--danger-soft)' };
};

/* ── Modal de Edição ── */
const EditModal = ({ assessment, configs, onClose, onSaved }) => {
  const [edited, setEdited]   = useState({ ...assessment });
  const [saving, setSaving]   = useState(false);
  const [alert, setAlert]     = useState(null);

  const active = configs.filter((c) => c.active);
  const score  = active.reduce((s, c) => s + (edited[`indicator_${c.slot}`] ? c.weight : 0), 0);
  const sem    = getSemaforo(score);

  const handleSave = async () => {
    setSaving(true);
    setAlert(null);
    try {
      const r = await api.put(`/assessment/${edited.id}`, edited);
      setAlert({ type: 'success', msg: 'Avaliação atualizada com sucesso!' });
      setTimeout(() => { onSaved(r.data); onClose(); }, 1000);
    } catch {
      setAlert({ type: 'danger', msg: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface-1)', borderRadius: 12, width: 560, maxWidth: '95vw',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '18px 24px 14px', borderBottom: '2px solid var(--accent)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>
            Editar Avaliação — {assessment.operator}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 18 }}>
            <LuX />
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {alert && (
            <div className={`alert-custom ${alert.type}`} style={{ marginBottom: 16 }}>{alert.msg}</div>
          )}

          {/* Campos de texto */}
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label className="form-label">Operador</label>
              <input
                className="form-control"
                value={edited.operator || ''}
                onChange={(e) => setEdited((p) => ({ ...p, operator: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Contratante</label>
              <input
                className="form-control"
                value={edited.contractor || ''}
                onChange={(e) => setEdited((p) => ({ ...p, contractor: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Mês de Referência</label>
              <input
                className="form-control"
                type="month"
                value={edited.reference_month || ''}
                onChange={(e) => setEdited((p) => ({ ...p, reference_month: e.target.value }))}
              />
            </div>
          </div>

          {/* Indicadores */}
          <div className="form-section-title" style={{ marginBottom: 12 }}>Indicadores</div>
          <div className="indicators-grid">
            {active.map((c) => (
              <label key={c.slot} className="indicator-check">
                <input
                  type="checkbox"
                  checked={edited[`indicator_${c.slot}`] || false}
                  onChange={(e) => setEdited((p) => ({ ...p, [`indicator_${c.slot}`]: e.target.checked }))}
                />
                <div className="indicator-check-label">
                  <span className="indicator-check-name">{c.name}</span>
                  <span className="indicator-check-weight">{c.weight} pts</span>
                </div>
              </label>
            ))}
          </div>

          {/* Placar ao vivo */}
          <div style={{
            marginTop: 16, padding: '10px 16px', borderRadius: 8,
            background: sem.bg, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div className={`semaforo-dot ${sem.cls}`} />
            <span style={{ fontWeight: 700, color: sem.color }}>{score > 0 ? `${score} pts — ` : ''}{sem.label}</span>
          </div>

          {/* Observações */}
          <div style={{ marginTop: 16 }}>
            <label className="form-label">Observações</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Observações adicionais (opcional)"
              value={edited.observations || ''}
              onChange={(e) => setEdited((p) => ({ ...p, observations: e.target.value }))}
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--surface-1)', cursor: 'pointer', fontSize: 13, color: 'var(--text-2)', fontFamily: 'inherit',
          }}>Cancelar</button>
          <button className="btn-salvar" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Card de Avaliação ── */
const AssessmentCard = ({ assessment, configs, onDelete, onEdit }) => {
  const active = configs.filter((c) => c.active);
  const score  = active.reduce((s, c) => s + (assessment[`indicator_${c.slot}`] ? c.weight : 0), 0);
  const sem    = getSemaforo(score);

  return (
    <div className="assessment-card-wrapper">
      <div className="assessment-card-header">
        <div>
          <div className="assessment-card-operator">{assessment.operator}</div>
          {assessment.contractor && (
            <div className="assessment-card-contractor">{assessment.contractor}</div>
          )}
          {assessment.department?.name && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{assessment.department.name}</div>
          )}
        </div>
        <div className="assessment-card-month">{assessment.reference_month}</div>
      </div>

      <div className={`semaforo-indicator ${sem.cls}`}>
        <div className={`semaforo-dot ${sem.cls}`} />
        {score > 0 ? `${score} pts — ` : ''}{sem.label}
      </div>

      {active.length > 0 ? (
        <div className="indicators-list">
          {active.map((c) => {
            const hit = assessment[`indicator_${c.slot}`];
            return (
              <div key={c.slot} className="indicator-row">
                <span>{c.name}</span>
                <span className={`indicator-value ${hit ? '' : 'zero'}`}>
                  {hit ? `${c.weight} pts` : '0 pts'}
                </span>
              </div>
            );
          })}
          <div className="indicator-row">
            <span><strong>Total</strong></span>
            <span className="indicator-value"><strong>{score} pts</strong></span>
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 0', color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>
          Indicadores não configurados para este departamento
        </div>
      )}

      <div className="assessment-card-actions">
        <button className="btn-editar" onClick={() => onEdit(assessment)}>
          <LuPencil style={{ marginRight: 4 }} /> Editar
        </button>
        <button className="btn-excluir" onClick={() => onDelete(assessment.id, assessment.operator)}>
          <LuTrash2 style={{ marginRight: 4 }} /> Excluir
        </button>
      </div>
    </div>
  );
};

/* ── Página Principal ── */
const AssessmentTable = () => {
  const [assessments, setAssessments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allConfigs, setAllConfigs]   = useState({});
  const [isLoading, setIsLoading]     = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterName, setFilterName]   = useState('');
  const [filterDept, setFilterDept]   = useState('');
  const [searched, setSearched]       = useState(false);
  const [editing, setEditing]         = useState(null); // assessment sendo editado

  useEffect(() => {
    api.get('/departments').then((r) => setDepartments(r.data)).catch(console.error);
  }, []);

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filterMonth) params.append('reference_month', filterMonth);
      if (filterName)  params.append('name', filterName);
      if (filterDept)  params.append('department_id', filterDept);

      const response = await api.get(`/assessment?${params.toString()}`);
      const data = response.data;

      // Carrega configs de todos os departamentos ANTES de exibir os cards
      const deptIds = [...new Set(data.map((a) => a.department_id).filter(Boolean))];
      const configMap = { ...allConfigs };
      await Promise.all(
        deptIds
          .filter((id) => !configMap[id])
          .map(async (id) => {
            const r = await api.get(`/departments/${id}/indicator-configs`);
            configMap[id] = r.data;
          })
      );

      setAllConfigs(configMap);
      setAssessments(data);
      setSearched(true);
    } catch (error) {
      console.error('Erro ao buscar avaliações:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir avaliação de "${name}"?`)) return;
    try {
      await api.delete(`/assessment/${id}`);
      setAssessments((prev) => prev.filter((a) => a.id !== id));
    } catch {
      alert('Erro ao excluir avaliação.');
    }
  };

  const handleSaved = (updated) => {
    setAssessments((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
  };


  return (
    <div className="page-card">
      {editing && (
        <EditModal
          assessment={editing}
          configs={allConfigs[editing.department_id] || []}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <div className="page-card-header">
        <div>
          <div className="page-card-title">Consultar Avaliações</div>
          <div className="page-card-subtitle">
            {assessments.length > 0
              ? `${assessments.length} avaliação(ões) encontrada(s)`
              : 'Use os filtros abaixo para buscar'}
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <div>
          <label className="form-label">Mês de Referência</label>
          <input
            className="form-control"
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <div>
          <label className="form-label">Nome do Operador</label>
          <input
            className="form-control"
            type="text"
            placeholder="Ex: João Silva"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <div>
          <label className="form-label">Departamento</label>
          <select
            className="form-control"
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
          >
            <option value="">Todos</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <button className="btn-buscar" onClick={handleSearch} disabled={isLoading}>
          <LuSearch style={{ marginRight: 6 }} />
          {isLoading ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      {assessments.length > 0 && (
        <div className="assessment-grid">
          {assessments.map((a) => (
            <AssessmentCard
              key={a.id}
              assessment={a}
              configs={allConfigs[a.department_id] || []}
              onDelete={handleDelete}
              onEdit={setEditing}
            />
          ))}
        </div>
      )}

      {searched && assessments.length === 0 && (
        <div className="empty-state">
          <LuClipboardX />
          <p>Nenhuma avaliação encontrada</p>
          <small>Tente ajustar os filtros e buscar novamente</small>
        </div>
      )}

      {!searched && (
        <div className="empty-state">
          <LuSearch />
          <p>Nenhuma busca realizada ainda</p>
          <small>Preencha os filtros e clique em Buscar</small>
        </div>
      )}
    </div>
  );
};

export default AssessmentTable;
